import * as amplitude from '@amplitude/analytics-browser';
import * as Castle from '@castleio/castle-js';
import { getAuth, getIdTokenResult, type User } from 'firebase/auth';
import Cookies from 'js-cookie';

import type { UserCustomClaims } from '@gitbook/app-database';
import type { EnabledFeatureFlagsMap } from '@gitbook/app-featureflags';
import { releaseVersion, webConfig } from '@gitbook/env';
import { getSentryGlobalScope, SentryTags } from '@gitbook/util-debug';

import {
    type AnalyticsFeatureFlagsProperties,
    type AnalyticsScreen,
    type ClientAnalyticsEvent,
    LOGIN_COOKIE,
    LOGIN_COOKIE_EXPIRATION_DAYS,
    type LoginCookieProfile,
    logger,
    prepareRawEvent,
    transformAmplitudeGroups,
} from '../common';
import { getIntercomUserHash } from './intercom';
import { getSegmentClient } from './segment';
import { getVisitorID, initializeVisitorID, syncVisitorSession } from './visitor-ids';

const WEB_VITALS_SAMPLE_RATE = 0.1;
const WEB_VITALS_EVENT_TYPE = '[Amplitude] Web Vitals';

// Stack of screens
let latestScreen: AnalyticsScreen | null = null;

// Latest initialized user
let latestIdentifiedUser: User['uid'] | null = null;

/**
 * Synchronize the login cookie with the current authenticated profile.
 */
export function syncLoginCookie(profile: LoginCookieProfile | null) {
    if (profile) {
        const { userId, organizationId } = profile;
        Cookies.set(LOGIN_COOKIE, JSON.stringify({ userId, organizationId }), {
            domain: webConfig.content.trackingCookieDomain,
            expires: LOGIN_COOKIE_EXPIRATION_DAYS,
            sameSite: 'none',
            secure: true,
        });
    } else {
        Cookies.remove(LOGIN_COOKIE, {
            domain: webConfig.content.trackingCookieDomain,
            sameSite: 'none',
            secure: true,
        });
    }

    // Ensure that the visitor session cookie is synchronized with the login state.
    void syncVisitorSession().catch((error) =>
        logger.critical('failed syncing visitor session', error)
    );
}

Castle.configure({
    pk: webConfig.castle.key,
    apiUrl: 'https://m.castle.io/v1/monitor',
});

initializeAnalytics();

async function initializeAnalytics() {
    const disableCookies = Boolean(process.env.GITBOOK_NO_ANALYTICS);

    // We synchronize with previous GitBook sessions, set the deviceId, and enable tracking.
    const visitorId = await initializeVisitorID(disableCookies);

    const { segment } = webConfig.analytics;

    if (segment.writeKey && !process.env.GITBOOK_NO_ANALYTICS) {
        logger.info(`initialize segment with visitorId "${visitorId}"`);
        getSegmentClient().setAnonymousId(visitorId);
        getSegmentClient().load(segment.writeKey);
    } else {
        logger.warning('tracking on segment is disabled because writeKey is not configured');
    }

    if (webConfig.analytics.amplitude.apiKey && !process.env.GITBOOK_NO_ANALYTICS) {
        logger.info(`initialize amplitude with visitorId "${visitorId}"`);
        const captureWebVitals = Math.random() < WEB_VITALS_SAMPLE_RATE;
        amplitude.init(webConfig.analytics.amplitude.apiKey, {
            serverUrl: webConfig.analytics.amplitude.endpoint,
            appVersion: releaseVersion,
            deviceId: visitorId,
            identityStorage: process.env.GITBOOK_NO_ANALYTICS ? 'none' : 'localStorage',

            // Auto-capture attribution and sampled Web Vitals, but disable the rest
            autocapture: {
                attribution: true,
                pageViews: false,
                sessions: false,
                formInteractions: false,
                fileDownloads: false,
                elementInteractions: false,
                webVitals: captureWebVitals,
            },
        });

        if (captureWebVitals) {
            amplitude.add(webVitalsDevicePlugin());
        }
    } else {
        logger.warning('tracking on amplitude is disabled because apiKey is not configured');
    }
}

/**
 * Track an event in Firebase analytics and Amplitude.
 */
export function logEvent(event: ClientAnalyticsEvent) {
    const url = new URL(window.location.href);
    const rawEvent = prepareRawEvent(event);

    logger.info(
        `log event "${rawEvent.type}"${
            process.env.GITBOOK_NO_ANALYTICS ? ' but GITBOOK_NO_ANALYTICS is set' : ''
        }`,
        rawEvent.context,
        rawEvent.properties
    );

    if (process.env.GITBOOK_NO_ANALYTICS) {
        return;
    }

    // we log everything to Amplitude
    const amplitudeProps = transformAmplitudeGroups(rawEvent.context);
    amplitude.track({
        event_type: rawEvent.type,
        event_properties: {
            ...rawEvent.properties,
            ...amplitudeProps.properties,
            screenDomain: url.hostname,
            screenPath: url.pathname,
            screenPageTitle: window.document.title,
            screenSite: 'app',
            screenURL: window.location.href,
        },
        groups: amplitudeProps.groups,
    });

    getSegmentClient().track(rawEvent.type, {
        ...rawEvent.properties,
        ...rawEvent.context,
    });

    if (rawEvent.context.organization) {
        getSegmentClient().group(rawEvent.context.organization);
    }

    if (event.type === 'screen_view') {
        getSegmentClient().page(
            event.screen,
            {},
            {
                integrations: {
                    All: true,
                    Intercom: {
                        hideDefaultLauncher: true,
                    },
                },
            }
        );
        const customerIoClient = getCustomerIoClient();
        if (customerIoClient) {
            customerIoClient.track('screen', {
                ...rawEvent.properties,
                ...rawEvent.context,
            });
        }
    }

    const auth = getAuth();
    const { currentUser } = auth;
    if (currentUser) {
        getIdTokenResult(currentUser).then((idTokenResult) => {
            const claims = idTokenResult.claims as UserCustomClaims;

            // Until we enforce "secure mode" in Castle.io, we can track using the user's id
            // if we don't have a signature ready (computed only during signin/signup).
            const castleUser = {
                ...(claims.castleSignature
                    ? {
                          userJwt: claims.castleSignature,
                      }
                    : {
                          user: {
                              id: currentUser.uid,
                              email: currentUser.email || undefined,
                              name: currentUser.displayName || undefined,
                          },
                      }),
            };

            switch (event.type) {
                case 'screen_view':
                    Castle.page({
                        url: url.toString(),
                        name: event.screen,
                        ...castleUser,
                    });
                    break;

                default:
                    // @ts-ignore castle is not typed correctly
                    Castle.custom({
                        name: rawEvent.type,
                        properties: Object.fromEntries(
                            Object.entries(rawEvent.properties).map(([key, value]) => [
                                key,
                                `${value}`,
                            ])
                        ),
                        ...castleUser,
                    });
                    break;
            }
        });
    }

    // Track in Google Tag Manager
    // To analyse ads conversion
    if (window.dataLayer?.push) {
        window.dataLayer.push({
            ...rawEvent.properties,
            event: rawEvent.type,
        });
    }
}

/**
 * Define the current screen.
 */
export function setCurrentScreen(screen: AnalyticsScreen) {
    logger.debug(`set current screen as "${screen}"`);
    latestScreen = screen;

    getSentryGlobalScope().setTag(SentryTags.UI_SCREEN, screen);
}

/**
 * Get the current screen.
 */
export function getCurrentScreen(): AnalyticsScreen | null {
    return latestScreen;
}

/**
 * Identify the current user into the analytics services.
 */
export async function identify({ flags }: { flags?: EnabledFeatureFlagsMap } = {}) {
    const auth = getAuth();
    const { currentUser } = auth;
    const userId = currentUser ? currentUser.uid : null;

    logger.info(
        `identify user as "${userId}"${
            process.env.GITBOOK_NO_ANALYTICS ? ' but GITBOOK_NO_ANALYTICS is set' : ''
        }`
    );

    if (process.env.GITBOOK_NO_ANALYTICS) {
        return;
    }

    if (!currentUser) {
        syncLoginCookie(null);
    }

    // Identify the user on the different services
    if (currentUser) {
        let userFeatureFlags: AnalyticsFeatureFlagsProperties | undefined;
        if (flags) {
            userFeatureFlags = {};
            Object.keys(flags).forEach((key) => {
                if (userFeatureFlags) {
                    userFeatureFlags[`featureFlag_${key}`] = flags[key];
                }
            });
            logger.info('tracking feature flags', userFeatureFlags);
        }

        amplitude.setUserId(currentUser.uid);
        if (userFeatureFlags) {
            const identifyEvent = new amplitude.Identify();
            Object.entries(userFeatureFlags).forEach(([key, value]) => {
                identifyEvent.set(key, value);
            });
            amplitude.identify(identifyEvent);
        }

        getSentryGlobalScope().setUser({
            id: currentUser.uid,
            email: currentUser.email || undefined,
            username: currentUser.displayName || undefined,
        });

        const intercomUserHash = await getIntercomUserHash(currentUser);

        getSegmentClient().identify(
            currentUser.uid,
            { email: currentUser.email || undefined, ...userFeatureFlags },
            {
                integrations: {
                    All: true,
                    Intercom: {
                        user_hash: intercomUserHash,
                    },
                },
            }
        );
        latestIdentifiedUser = currentUser.uid;
    } else {
        getSentryGlobalScope().setUser(null);

        if (latestIdentifiedUser) {
            getSegmentClient().reset();
            getSegmentClient().setAnonymousId(getVisitorID());
        }
        latestIdentifiedUser = null;
    }
}

/**
 * Return the customer.io JS client if initialized
 */
function getCustomerIoClient() {
    return typeof window._cio !== 'undefined' ? window._cio : null;
}

/**
 * Tag Web Vitals events with the visitor's connection and hardware, to segment slow devices.
 */
function webVitalsDevicePlugin(): amplitude.Types.EnrichmentPlugin {
    return {
        name: 'gitbook-web-vitals-device',
        type: 'enrichment',
        execute: async (event) => {
            if (event.event_type !== WEB_VITALS_EVENT_TYPE) {
                return event;
            }

            // Chromium-only APIs, missing from the DOM typings.
            const nav = navigator as Navigator & {
                connection?: {
                    effectiveType?: string;
                    downlink?: number;
                    rtt?: number;
                    saveData?: boolean;
                };
                deviceMemory?: number;
            };

            event.event_properties = {
                ...event.event_properties,
                connectionType: nav.connection?.effectiveType,
                connectionDownlinkMbps: nav.connection?.downlink,
                connectionRttMs: nav.connection?.rtt,
                saveData: nav.connection?.saveData,
                cpuCores: nav.hardwareConcurrency,
                deviceMemoryGb: nav.deviceMemory,
            };
            return event;
        },
    };
}
